/** SUNTRONIX ONE — Location Inventory 360 (Phase 1).
 *
 *  Assets currently held at a Customer location with quick actions:
 *  Assign to Employee · Reserve · Mark Shared · Change Custodian ·
 *  Release Reservation.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Building2, RefreshCcw, UserPlus, Bookmark, Users2, User,
  X, Package, Search,
} from "lucide-react";

const TABS = ["all", "available", "reserved", "shared", "assigned"];
const TAB_LABEL = {
  all: "All", available: "Available", reserved: "Reserved",
  shared: "Shared", assigned: "Assigned",
};

const AVAIL_TONE = {
  AVAILABLE: "bg-emerald-100 text-emerald-800",
  RESERVED:  "bg-amber-100 text-amber-900",
  SHARED:    "bg-fuchsia-100 text-fuchsia-800",
  IN_USE:    "bg-sky-100 text-sky-800",
};


export default function LocationInventory() {
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [inventory, setInventory] = useState({ items: [], counts: {} });
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [assign, setAssign] = useState(null);   // asset row
  const [reserve, setReserve] = useState(null);
  const [share, setShare] = useState(null);
  const [employees, setEmployees] = useState([]);

  const load = useCallback(async () => {
    if (!locationId) { setInventory({ items: [], counts: {} }); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/office-deployment/location/${locationId}/inventory`);
      setInventory(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, [locationId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/sites").then(({ data }) => setLocations(data.items || []))
      .catch(() => api.get("/locations").then(({ data }) => setLocations(data.items || [])).catch(() => {}));
  }, []);

  useEffect(() => {
    if (!assign) return;
    api.get("/employees", { params: { master_company_id: assign.master_company_id || undefined, limit: 500 } })
      .then(({ data }) => setEmployees(data.items || [])).catch(() => {});
  }, [assign]);

  const filtered = useMemo(() => {
    const rows = inventory.items || [];
    const byTab = rows.filter((r) => {
      if (tab === "available") return r.availability === "AVAILABLE";
      if (tab === "reserved") return r.availability === "RESERVED";
      if (tab === "shared") return r.availability === "SHARED";
      if (tab === "assigned") return r.assignment_status === "FULLY_ASSIGNED";
      return true;
    });
    if (!q.trim()) return byTab;
    const qq = q.toLowerCase();
    return byTab.filter((r) =>
      (r.serial || "").toLowerCase().includes(qq) ||
      (r.asset_tag || "").toLowerCase().includes(qq) ||
      (r.model || "").toLowerCase().includes(qq) ||
      (r.brand || "").toLowerCase().includes(qq));
  }, [inventory, tab, q]);

  const doAssign = async (employee_id, assignment_type) => {
    if (!assign || !employee_id) return;
    try {
      await api.post("/office-deployment/assign", {
        asset_id: assign.id, employee_id, assignment_type,
      });
      toast.success("Asset assigned to employee");
      setAssign(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
  };

  const doReserve = async (payload) => {
    try {
      await api.post("/office-deployment/reserve", { asset_id: reserve.id, ...payload });
      toast.success("Asset reserved");
      setReserve(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reserve failed"); }
  };

  const doShare = async (payload) => {
    try {
      await api.post("/office-deployment/share", { asset_id: share.id, ...payload });
      toast.success("Marked as shared device");
      setShare(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Mark shared failed"); }
  };

  const doRelease = async (asset_id) => {
    if (!window.confirm("Release this reservation?")) return;
    try {
      await api.post("/office-deployment/release-reservation", { asset_id });
      toast.success("Reservation released"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Release failed"); }
  };

  return (
    <div className="space-y-6 max-w-[1600px]" data-testid="location-inventory-page">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
            Customer Location 360
          </div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
            <Building2 className="h-6 w-6 text-slate-700" strokeWidth={1.6} />
            Location Inventory
          </h1>
          <p className="text-sm text-slate-600 mt-1.5 max-w-2xl">
            Assets currently held at a Customer office. Assign to an employee, reserve
            for a future joiner, or mark as a shared device — the Golden Asset Record
            preserves every custody change.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={locationId} onValueChange={setLocationId}>
            <SelectTrigger className="h-10 w-80" data-testid="loc-inv-picker">
              <SelectValue placeholder="Pick a Customer location…" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((l) => (
                <SelectItem key={l.id} value={l.id}>
                  {l.name || l.code || l.id} {l.city ? `· ${l.city}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={!locationId || busy}
            className="h-10" data-testid="loc-inv-reload">
            <RefreshCcw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} strokeWidth={1.6} />
            Reload
          </Button>
        </div>
      </div>

      {locationId && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Total" value={inventory.counts?.total || 0} testId="kpi-total" />
            <Kpi label="Available" value={inventory.counts?.available || 0} tone="emerald" testId="kpi-avail" />
            <Kpi label="Reserved" value={inventory.counts?.reserved || 0} tone="amber" testId="kpi-res" />
            <Kpi label="Shared" value={inventory.counts?.shared || 0} tone="fuchsia" testId="kpi-sh" />
            <Kpi label="Assigned" value={inventory.counts?.assigned || 0} tone="sky" testId="kpi-asg" />
          </div>

          <div className="bg-white border border-slate-200 rounded-md p-3 flex items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Serial · asset tag · model…"
                data-testid="loc-inv-search"
                className="h-9 pl-8" />
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {TABS.map((t) => (
                <button key={t} onClick={() => setTab(t)}
                  data-testid={`loc-inv-tab-${t}`}
                  className={
                    "h-8 px-3 rounded-md text-[12px] font-medium " +
                    (tab === t ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100")
                  }>
                  {TAB_LABEL[t]}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Asset</th>
                  <th className="text-left px-3 py-2">Serial / Tag</th>
                  <th className="text-left px-3 py-2">Custodian</th>
                  <th className="text-left px-3 py-2">Assignee</th>
                  <th className="text-left px-3 py-2">Availability</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-8 text-slate-500">
                    {busy ? "Loading…" : "No assets match this filter."}
                  </td></tr>
                ) : filtered.map((r) => (
                  <tr key={r.id} data-testid={`inv-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="text-slate-900">{r.brand} {r.model}</div>
                      <div className="text-[11px] text-slate-500">{r.category || "—"}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[12px] text-slate-700">
                      {r.serial || "—"}<br />
                      <span className="text-slate-400">{r.asset_tag || ""}</span>
                    </td>
                    <td className="px-3 py-2 text-[12px] text-slate-700">
                      {r.current_custodian?.name || "—"}
                    </td>
                    <td className="px-3 py-2 text-[12px] text-slate-700">
                      {r.assigned_employee_name || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span className={"h-5 px-2 rounded-full text-[10px] font-medium uppercase " +
                        (AVAIL_TONE[r.availability] || "bg-slate-100 text-slate-700")}>
                        {r.availability || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1.5">
                        {r.assignment_status !== "FULLY_ASSIGNED" && (
                          <>
                            <Button size="sm" variant="outline" className="h-8"
                              onClick={() => setAssign(r)}
                              data-testid={`inv-assign-${r.id}`}>
                              <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Assign
                            </Button>
                            {r.availability !== "SHARED" && (
                              <Button size="sm" variant="outline" className="h-8"
                                onClick={() => setShare(r)}
                                data-testid={`inv-share-${r.id}`}>
                                <Users2 className="h-3.5 w-3.5 mr-1.5" /> Share
                              </Button>
                            )}
                            {r.availability === "RESERVED" ? (
                              <Button size="sm" variant="ghost" className="h-8 text-slate-500"
                                onClick={() => doRelease(r.id)}>
                                <X className="h-3.5 w-3.5 mr-1.5" /> Release
                              </Button>
                            ) : r.availability !== "SHARED" && (
                              <Button size="sm" variant="outline" className="h-8"
                                onClick={() => setReserve(r)}
                                data-testid={`inv-reserve-${r.id}`}>
                                <Bookmark className="h-3.5 w-3.5 mr-1.5" /> Reserve
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {assign && (
        <AssignDialog row={assign} employees={employees}
          onCancel={() => setAssign(null)} onSave={doAssign} />
      )}
      {reserve && (
        <ReserveDialog row={reserve}
          onCancel={() => setReserve(null)} onSave={doReserve} />
      )}
      {share && (
        <ShareDialog row={share}
          onCancel={() => setShare(null)} onSave={doShare} />
      )}
    </div>
  );
}


function Kpi({ label, value, tone = "slate", testId }) {
  const map = { slate:"text-slate-900", emerald:"text-emerald-700",
    amber:"text-amber-800", fuchsia:"text-fuchsia-700", sky:"text-sky-700" };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className={"text-2xl font-heading font-semibold mt-1 " + (map[tone] || map.slate)}>
        {value}
      </div>
    </div>
  );
}


function AssignDialog({ row, employees, onCancel, onSave }) {
  const [empId, setEmpId] = useState("");
  const [type, setType] = useState("PERMANENT_EMPLOYEE");
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-lg" data-testid="assign-dialog">
        <DialogHeader><DialogTitle>Assign to Employee</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-[12px] text-slate-600">
            {row.brand} {row.model} · <span className="font-mono">{row.serial}</span>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Employee *</div>
            <Select value={empId} onValueChange={setEmpId}>
              <SelectTrigger className="h-10" data-testid="assign-employee-select">
                <SelectValue placeholder="Pick an employee…" />
              </SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name} · {e.email || e.department || ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Assignment type</div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["PERMANENT_EMPLOYEE","TEMPORARY_EMPLOYEE","LOANER","PROJECT",
                  "DEPARTMENT","LOCATION_BASED"].map((t) =>
                  <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onSave(empId, type)} disabled={!empId}
            className="bg-slate-900 text-white hover:bg-slate-800"
            data-testid="assign-save">
            <User className="h-4 w-4 mr-1.5" /> Assign
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ReserveDialog({ row, onCancel, onSave }) {
  const [form, setForm] = useState({
    reserved_for_type: "EMPLOYEE", reserved_for_ref: "",
    reservation_reason: "", expiry_date: "",
  });
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-lg" data-testid="reserve-dialog">
        <DialogHeader><DialogTitle>Reserve asset</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-[12px] text-slate-600">
            {row.brand} {row.model} · <span className="font-mono">{row.serial}</span>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Reserved for *</div>
            <Select value={form.reserved_for_type}
              onValueChange={(v) => setForm({ ...form, reserved_for_type: v })}>
              <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["EMPLOYEE","PROJECT","DEPARTMENT","REPLACEMENT",
                  "NEW_JOINER","VIP","EMERGENCY"].map((t) =>
                  <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Reference (employee id / project / etc.)</div>
            <Input value={form.reserved_for_ref}
              onChange={(e) => setForm({ ...form, reserved_for_ref: e.target.value })} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Reason</div>
            <Input value={form.reservation_reason}
              onChange={(e) => setForm({ ...form, reservation_reason: e.target.value })} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Expiry date</div>
            <Input type="date" value={form.expiry_date}
              onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onSave(form)}
            className="bg-slate-900 text-white hover:bg-slate-800"
            data-testid="reserve-save">Reserve</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ShareDialog({ row, onCancel, onSave }) {
  const [form, setForm] = useState({
    purpose: "", room_or_area: "", department: "",
    custodian_name: "", custodian_email: "",
    checkout_required: false,
  });
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-lg" data-testid="share-dialog">
        <DialogHeader><DialogTitle>Mark as Shared device</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="text-[12px] text-slate-600">
            {row.brand} {row.model} · <span className="font-mono">{row.serial}</span>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Purpose *</div>
            <Input value={form.purpose}
              placeholder="Conference room device / Warehouse scanner / etc."
              onChange={(e) => setForm({ ...form, purpose: e.target.value })}
              data-testid="share-purpose" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Room / area</div>
              <Input value={form.room_or_area}
                onChange={(e) => setForm({ ...form, room_or_area: e.target.value })} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Department</div>
              <Input value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Custodian name</div>
              <Input value={form.custodian_name}
                onChange={(e) => setForm({ ...form, custodian_name: e.target.value })} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500 mb-1">Custodian email</div>
              <Input value={form.custodian_email}
                onChange={(e) => setForm({ ...form, custodian_email: e.target.value })} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.checkout_required}
              onChange={(e) => setForm({ ...form, checkout_required: e.target.checked })} />
            Requires checkout / return
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={!form.purpose}
            className="bg-slate-900 text-white hover:bg-slate-800"
            data-testid="share-save">
            <Package className="h-4 w-4 mr-1.5" /> Mark shared
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
